// Phase 2.5 — REST-backed sessionService.
//
// Mirrors the legacy sessionService surface that lives in firebaseService.js.
// SABAK auto-promotion to student.memorizedPortions runs server-side now;
// the wirdRatings mirror is deferred to Phase 2.13 when wirdRatings is
// migrated end-to-end. _legacyLogToSession bridging is kept intact (still
// uses dailyLogs for historical tail until 2.13).

import { getAccessToken } from '../contexts/AuthContextV2';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4001';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// Match the legacy DEFAULT_QUALITY_RATING shape (each axis 0–5, overall mean).
const DEFAULT_QUALITY_RATING = { accuracy: null, fluency: null, tajweed: null, overall: null };

function computeOverallFromAxes(rating) {
  const axes = ['accuracy', 'fluency', 'tajweed'];
  const present = axes.filter((a) => typeof rating[a] === 'number');
  if (present.length === 0) return null;
  return Math.round((present.reduce((s, a) => s + rating[a], 0) / present.length) * 100) / 100;
}

export const sessionService = {
  normalizeQualityRating(rating) {
    const next = { ...DEFAULT_QUALITY_RATING, ...(rating || {}) };
    if (next.overall === null || next.overall === undefined) {
      next.overall = computeOverallFromAxes(next);
    }
    return next;
  },

  async createSession(sessionData) {
    const quality = sessionService.normalizeQualityRating(sessionData.qualityRating);
    const body = {
      studentId: sessionData.studentId,
      classId: sessionData.classId ?? null,
      date: sessionData.date,
      type: sessionData.type,
      portionsAffected: sessionData.portionsAffected ?? [],
      portionVerses: sessionData.portionVerses ?? null,
      qualityRating: quality,
      teacherNotes: sessionData.teacherNotes ?? '',
      attendance: sessionData.attendance ?? 'present',
      mistakes: sessionData.mistakes ?? null,
      tajweedTags: Array.isArray(sessionData.tajweedTags) ? sessionData.tajweedTags : [],
      tajweedRemarks: Array.isArray(sessionData.tajweedRemarks) ? sessionData.tajweedRemarks : null,
      audioUrl: sessionData.audioUrl ?? null
    };
    const { session } = await apiFetch('/api/sessions', { method: 'POST', body: JSON.stringify(body) });
    return session.id;
  },

  async rateSession(sessionId, qualityRating) {
    const quality = sessionService.normalizeQualityRating(qualityRating);
    await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ qualityRating: quality })
    });
  },

  async updateSession(sessionId, patch) {
    const updates = { ...patch };
    if (patch.qualityRating) updates.qualityRating = sessionService.normalizeQualityRating(patch.qualityRating);
    await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  },

  async deleteSession(sessionId) {
    await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  },

  async getSessionsByStudent(studentId, max = 100) {
    const { sessions } = await apiFetch(`/api/sessions?studentId=${encodeURIComponent(studentId)}&limit=${max}`);
    return sessions;
  },

  async getSessionsByDate(date, classId = null) {
    const q = classId
      ? `/api/sessions?date=${encodeURIComponent(date)}&classId=${encodeURIComponent(classId)}`
      : `/api/sessions?date=${encodeURIComponent(date)}`;
    const { sessions } = await apiFetch(q);
    return sessions;
  }
};

export default sessionService;
